import { Injectable } from '@angular/core';

import { Nullable } from '@frontend/sports/common/core/utils/extended-types';
import { Observable, combineLatest, concatMap, distinctUntilChanged, map, merge, startWith } from 'rxjs';

import { BetslipIntegrationService } from '../../integration/betslip-integration.service';
import { INotification } from '../../modules/acca-boost/model';
import { BetslipReturnsMessagesAccaBoostService } from './betslip-returns-messages-acca-boost.service';
import { BetslipReturnsMessagesVisibilityService } from './betslip-returns-messages-visbility.service';
import { MultiReturnsMessageNotificationService } from './multi-returns-message-notification.service';
import { RemindMeLaterService } from './remind-me-later.service';

export interface BetslipReturnNotification {
    accaBoost: Nullable<INotification>;
    multiReturn: Nullable<string>;
    tokensReminder: Nullable<boolean>;
}

@Injectable()
export class BetslipReturnsMessagesNotificationService {
    readonly notifications$: Observable<BetslipReturnNotification>;

    constructor(
        betslipIntegration: BetslipIntegrationService,
        multiReturnNotificationService: MultiReturnsMessageNotificationService,
        returnsMessagesVisibilityService: BetslipReturnsMessagesVisibilityService,
        returnsMessagesAccaBoostService: BetslipReturnsMessagesAccaBoostService,
        remaindMeLaterService: RemindMeLaterService,
    ) {
        const accaBoost$ = returnsMessagesAccaBoostService.accaBoostNotifications$;
        const multiReturn$ = multiReturnNotificationService.getMessage$().pipe(startWith(null));
        const tokensReminder$ = remaindMeLaterService.remindMeNotification$;

        const model$ = combineLatest([accaBoost$, multiReturn$, tokensReminder$]).pipe(
            map(([accaBoost, multiReturn, tokensReminder]) => {
                return {
                    accaBoost,
                    multiReturn,
                    tokensReminder,
                };
            }),
        );

        const visible$ = returnsMessagesVisibilityService.isVisible$();
        const hideOnPopupOpen$ = returnsMessagesVisibilityService.hideOnPopupOpen$().pipe(map((_) => this.emptyModel));

        const visibleModel$ = combineLatest([model$, visible$]).pipe(
            map(([model, visible]) => (visible ? model : this.emptyModel)),
            distinctUntilChanged(),
        );

        this.notifications$ = betslipIntegration.betslipInitialized$().pipe(
            concatMap(() => {
                return merge(hideOnPopupOpen$, visibleModel$);
            }),
        );
    }

    private get emptyModel(): BetslipReturnNotification {
        return { accaBoost: null, multiReturn: null, tokensReminder: null };
    }
}
